import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { validLangs } from '@/mocks/languages';
import { mainSiteUrl } from '@/mocks/categories';
import { articles, type Localized } from '@/mocks/articles';

type Lang = 'en' | 'fr' | 'da' | 'de';

export default function Article() {
  const { lang, slug } = useParams();
  const { t, i18n } = useTranslation();
  const currentLang = (lang && validLangs.includes(lang) ? lang : 'en') as Lang;

  useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const pick = (v: Localized) => v[currentLang];

  const article = articles.find((a) => a.slug === slug);
  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="text-center">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              {t('article.breadcrumb')}
            </h1>
            <p className="mt-3 text-foreground-600">404</p>
            <Link
              to={`/${currentLang}`}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to={`/${currentLang}`} className="hover:text-primary-600 transition-colors">
              {t('article.breadcrumb')}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{pick(article.category)}</span>
          </nav>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <header className="mb-8">
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 font-medium">
                {pick(article.category)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {pick(article.readTime)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {t('article.updated')}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              {pick(article.title)}
            </h1>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <div className="w-full h-52 md:h-72">
              <img
                src={article.image}
                alt={pick(article.title)}
                title={`${pick(article.title)} AllChinaBuy Spreadsheet`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="text-foreground-700 leading-relaxed">
            <p className="text-base md:text-lg text-foreground-700">{pick(article.intro)}</p>

            {article.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-8 mb-3">
                  {pick(s.heading)}
                </h2>
                <p>{pick(s.body)}</p>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              {pick(article.faqTitle)}
            </h2>
            <div className="flex flex-col gap-3">
              {article.faq.map((f, i) => (
                <div key={i} className="rounded-lg bg-background-100 border border-background-200/70 p-4">
                  <p className="font-medium text-foreground-900 text-sm md:text-base">{pick(f.q)}</p>
                  <p className="mt-1 text-foreground-600 text-sm">{pick(f.a)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related articles */}
          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
                {t('article.relatedTitle')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/${currentLang}/${r.slug}`}
                    className="group rounded-lg overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full h-24 overflow-hidden">
                      <img
                        src={r.image}
                        alt={pick(r.title)}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading font-semibold text-foreground-950 text-sm leading-snug">
                        {pick(r.title)}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="mt-10 rounded-lg bg-primary-500 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-background-50">
              {t('article.ctaTitle')}
            </h2>
            <p className="mt-2 text-background-50/90 text-sm">{t('article.ctaSubtitle')}</p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-700 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
            >
              {t('article.ctaButton')}
              <i className="ri-external-link-line"></i>
            </a>
          </section>

          {/* Internal link back home */}
          <div className="mt-8 flex justify-center">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}